import type { EnsureFulfillmentForAttioValuationRequest } from 'api/models/EnsureFulfillmentForAttioValuationRequest';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { dbShortDate } from 'utilities';
import type { FulfillmentSideEffectsResult } from './fulfillmentOnStatusChange';

/** Measurement date for CMD: valuation date from valued information (not deal date). */
export function parseValuationDateToIso(valuationDate: string | undefined | null): string | null {
  if (!valuationDate || valuationDate === '-') {
    return null;
  }
  return dbShortDate(valuationDate.trim());
}

function resolveAttioCompanyId(
  valuationDetail: AttioValuationDetailResponse,
  valuationId: string,
  modalData: unknown
): string | undefined {
  if (modalData && typeof modalData === 'object' && 'attio_company_id' in modalData) {
    const raw = (modalData as { attio_company_id?: string | null }).attio_company_id;
    if (raw) {
      return String(raw);
    }
  }
  const veId = valuationDetail.valuedEntity?.valuedEntityId;
  if (veId && veId !== '-' && veId !== valuationId) {
    return veId;
  }
  return undefined;
}

/** Base body for POST .../ensure-fulfillment-for-attio-valuation/ */
export function buildEnsureFulfillmentBaseRequest(params: {
  valuationDetail: AttioValuationDetailResponse;
  valuationId: string;
  modalData: unknown;
}): EnsureFulfillmentForAttioValuationRequest | null {
  const { valuationDetail, valuationId, modalData } = params;

  const measurementDateIso = parseValuationDateToIso(valuationDetail.valuedInformation?.valuationDate);
  if (!measurementDateIso) {
    return null;
  }

  const name =
    valuationDetail.valuedEntity?.companyName?.trim() ||
    valuationDetail.valuedInformation?.valuationName?.trim() ||
    'Company';

  const attioCompanyIdResolved = resolveAttioCompanyId(valuationDetail, valuationId, modalData);

  const payload: EnsureFulfillmentForAttioValuationRequest = {
    attio_valuation_id: valuationId,
    name,
    measurement_date: measurementDateIso,
  };

  if (attioCompanyIdResolved) {
    payload.attio_company_id = attioCompanyIdResolved;
  }

  return payload;
}

export function mergeFulfillmentSideEffectsIntoEnsureRequest(
  base: EnsureFulfillmentForAttioValuationRequest,
  side: FulfillmentSideEffectsResult
): EnsureFulfillmentForAttioValuationRequest {
  if (side.action === 'create' && side.createPayload) {
    const c = side.createPayload;
    return {
      ...base,
      model_status: c.model_status,
      supporting_due_date: c.supporting_due_date,
      ...(c.is_slc_model !== undefined && { is_slc_model: c.is_slc_model }),
      reviewers: c.reviewers,
      preparers: c.preparers,
    };
  }

  if (side.action === 'update' && side.updatePayload) {
    const u = side.updatePayload;
    return {
      ...base,
      ...(u.model_status !== undefined && { model_status: u.model_status }),
      ...(u.supporting_due_date !== undefined && { supporting_due_date: u.supporting_due_date }),
      ...(u.is_slc_model !== undefined && { is_slc_model: u.is_slc_model }),
      ...(u.is_waiting !== undefined && { is_waiting: u.is_waiting }),
    };
  }

  return { ...base };
}
